import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import useFormData from '@client/module/utils/use-form-data.ts';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import AlertError from '@packages/ui/alert/alert-error.tsx';
import { useCallback } from 'react';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import { formatGraphQlError, validationErrors } from '@client/module/error/error.ts';
import { useCreateTableGroup } from '@client/page/management/table-group/table-group-create/logic/use-create-table-group';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useTranslate } from '@tolgee/react';
import { useNotificationCenter } from 'ui/src/notification/notification-center';

interface TableGroupForm {
  name: string;
  arrangementCode: string;
}

export default function TableGroupCreate(props: { branchId: string; onClose?: () => void; refetch: () => void }) {
  const { branchId } = props;

  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const { data: form, handle } = useFormData<TableGroupForm>({
    name: '',
    arrangementCode: '',
  });

  const [create, { error, loading }] = useCreateTableGroup();
  const applicationErrors = formatGraphQlError(error?.graphQLErrors);
  const validationError = validationErrors(applicationErrors);

  const submit = useCallback(() => {
    create({
      variables: {
        branchID: branchId,
        name: form.name,
        arrangementCode: form.arrangementCode,
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          title: t('table-group.create.alert'),
          message: t('table-group.create.alert.message'),
        });
        props.onClose?.();
        props.refetch();
      })
      .catch((e: Error) => {
        showAlert({
          status: 'error',
          title: t('table-group.create.error-alert'),
          message: e.message,
        });
      });
  }, [branchId, create, form.arrangementCode, form.name, props, showAlert, t]);

  return (
    <Card>
      <CardHeader title={t('table-group-list.create-table-group')} />

      <CardContent>
        <FormLayout>
          <FormItem className="max-w-md" title={t('common.name')}>
            <TextInput
              type="text"
              name="name"
              label="name"
              autoComplete="off"
              placeholder={t('common.name')}
              error={validationError.name}
              value={form.name}
              onChange={handle}
            />
          </FormItem>

          <FormItem className="max-w-md" title={t('common.arrangement-code')}>
            <TextInput
              type="text"
              name="arrangementCode"
              label="arrangementCode"
              autoComplete="off"
              placeholder={t('common.arrangement-code')}
              error={validationError.name}
              value={form.arrangementCode}
              onChange={handle}
            />
          </FormItem>
        </FormLayout>

        {error && <AlertError className="mt-4" title={'Error'} message={error.message} />}
      </CardContent>

      <CardFooter>
        <div className="flex justify-center space-x-4">
          <SecondaryButton className="w-32" onClick={props.onClose}>
            {t('common.close')}
          </SecondaryButton>
          <PrimaryButton
            id="create-button"
            data-testid="create-button"
            className="w-32"
            onClick={submit}
            loading={loading}
          >
            {t('common.button.create')}
          </PrimaryButton>
        </div>
      </CardFooter>
    </Card>
  );
}
